import { useEffect, useMemo, useState } from 'react';
import { CircularProgress } from '@mui/material';

import { CoreBox, CoreTooltip, CoreTypography } from '@youscience/core';
import {
  Member,
  MembersDocument,
  RecommenderDocument,
  Roles,
  SubjectArea,
  TeacherRecommendationStatus,
} from '@youscience/commonapp-service-common';

import useCommonAppStore from '@store/CommonAppStore';
import useCommonAppAdminStore from '@store/CommonAppAdminStore';
import useRecommenderStore from '@store/RecommenderStore';

import ActionButton from '@components/ActionButton';

import AddTeacher from '@pages/EducationPlannerApplications/components/CommonApp/AddTeacher';
import UpdateTeacher from '@pages/EducationPlannerApplications/components/CommonApp/UpdateTeacher';
import TeacherCard from '@pages/EducationPlannerApplications/components/CommonApp/TeacherCard';
import CounselorFormsStatus from '@pages/EducationPlannerApplications/components/CommonApp/CounselorFormStatus';

import AddIcon from '@images/plus-button.svg?react';
import InfoIcon from '@images/info.svg?react';

import {
  StyledDoneAllIcon,
  StyledJustifiedBox,
  StyledRecommenders,
  StyledTeacherButtonGroup,
  StyledTeacherCountGroup,
  StyledTooltipBox,
} from './PlannerSchoolCardRecommenders.styles';

interface ApplicationRecommendersProp {
  isMobileScreen: boolean;
  member: Member;
}

export const PlannerSchoolCardRecommenders = (props: ApplicationRecommendersProp) => {
  const { member } = props;

  const { applicant, unassignTeacher, getMembers, recommenders, assignTeacher, inviteTeacher, editTeacher, isLoading } =
    useCommonAppStore();

  const { getRecommenders } = useRecommenderStore((store) => store);

  const { roles, getRoles } = useCommonAppAdminStore((store) => store);

  const { formStatuses } = useCommonAppStore((store) => store);

  const [members, setMembers] = useState<MembersDocument[]>([]);

  const [showAddTeacher, setShowAddTeacher] = useState(false);
  const [showEditTeacher, setShowEditTeacher] = useState<RecommenderDocument | null>(null);

  const counselor = useMemo(
    () => member?.recommenders?.find((recommender) => recommender.personaType === 'Counselor'),
    [member],
  );

  const teachers = useMemo(
    () => member?.recommenders?.filter((recommender) => recommender.personaType === 'Teacher'),
    [member],
  );

  const teacherIds = useMemo(() => new Set(teachers?.map((teacher) => teacher.recommenderId)), [teachers]);

  const recommendersNotAssigned = useMemo(
    () => applicant?.teachers?.filter((teacher) => !teacherIds.has(teacher.recommenderId)) || [],
    [applicant, teacherIds],
  );

  const teacherRolesNotAssigned = useMemo(
    () =>
      roles?.filter(
        (role) =>
          role.roles?.includes(Roles.TEACHER) &&
          !!role.email &&
          !applicant?.teachers?.some((teacher) => teacher.userId === role.userId),
      ) ?? [],
    [roles, applicant],
  );

  const { minTeacherEval = 0, maxTeacherEval = 0 } = (members[0] ?? {}) as MembersDocument;

  const teachersAssigned = teachers?.length || 0;

  const handleAssignTeacher = async (recommenderId: number) => {
    const result = await assignTeacher(recommenderId, member.memberId);

    setShowAddTeacher(false);

    return result;
  };

  const handleAddAndAssignTeacher = async (userId: string, subject: SubjectArea) => {
    await inviteTeacher(userId, subject, member.memberId);

    setShowAddTeacher(false);
  };

  const onHandleSaveTeacher = async (recommenderId: number, subject: string) => {
    await editTeacher(recommenderId, subject);

    setShowEditTeacher(null);
  };

  const onHandleRemoveTeacher = async (recommenderId: number) => {
    await unassignTeacher(recommenderId, member.memberId);

    setShowAddTeacher(false);
  };

  const onHandleEditTeacher = async (recommenderId: number) => {
    const teacher = applicant?.teachers?.find((t) => t.recommenderId === recommenderId);

    if (teacher) {
      setShowEditTeacher(teacher);
    }
  };

  useEffect(() => {
    if (!recommenders) {
      void getRecommenders();
    }

    void getRoles();
  }, []);

  useEffect(() => {
    const getMembersList = async () => {
      setMembers(await getMembers([member.memberId]));
    };

    if (member?.memberId) {
      void getMembersList();
    }
  }, [member]);

  const formMemberStatus = formStatuses.members.find((m) => m.memberId === member.memberId);

  const allRequiredCounselorFormsSubmitted =
    formMemberStatus?.counselor?.actionsRequired?.length === 1 &&
    formMemberStatus.counselor.actionsRequired[0] === 'Actions completed';

  const allRequiredTeacherFormsSubmitted = formMemberStatus?.teachers?.every(
    (teacher) => teacher.status === TeacherRecommendationStatus.SUBMITTED,
  );

  return applicant ? (
    <StyledRecommenders>
      <CoreBox sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
        <CoreTypography variant="h5" component="h4">
          Recommenders
        </CoreTypography>
        {allRequiredCounselorFormsSubmitted && allRequiredTeacherFormsSubmitted ? (
          <CoreBox sx={{ display: 'flex', alignItems: 'center', gap: 1 }} data-testid="completed-recommenders">
            <StyledDoneAllIcon />
            <CoreTypography sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Completed</CoreTypography>
          </CoreBox>
        ) : null}
      </CoreBox>
      <CoreBox sx={{ display: 'grid', gap: 1, gridTemplateColumns: '1fr auto', width: '100%' }}>
        <CoreTypography>
          Common App recommenders are people who can provide insight into your academic performance and potential fit
          for a college.
        </CoreTypography>
      </CoreBox>
      {isLoading ? (
        <CircularProgress sx={{ position: 'absolute', left: '50%', transform: 'translate(0%, -50%)' }} />
      ) : null}
      {counselor ? <CounselorFormsStatus counselor={counselor} /> : null}
      {minTeacherEval > 0 || maxTeacherEval > 0 ? (
        <>
          <StyledJustifiedBox>
            <StyledTooltipBox>
              <CoreTypography variant="h6" component="h5">
                Teachers{' '}
              </CoreTypography>
              <CoreTooltip
                title="
          Teachers provide information about you in the context of their classroom. They have firsthand knowledge of your intellectual
           curiosity, creative thought, and writing quality. They can also contextualize your class behavior and work habits. First, ask
           teachers in person or by email if they will provide an evaluation (letter of recommendation) before adding them here. Then,
           give them at least three weeks before the application deadline so they have enough time to submit a well-written evaluation."
              >
                <CoreTypography>
                  <InfoIcon />
                </CoreTypography>
              </CoreTooltip>
            </StyledTooltipBox>
            <StyledTooltipBox>
              <CoreTooltip title="Every college can choose how many evaluations (letters of recommendation) to require. They can also determine which recommender types they accept.">
                <CoreTypography>
                  <InfoIcon />
                </CoreTypography>
              </CoreTooltip>
              <StyledTeacherCountGroup>
                <CoreTypography variant="body2">
                  <b>Required:</b> {`${Math.min(teachersAssigned, minTeacherEval)}/${minTeacherEval}`}
                </CoreTypography>
                <CoreTypography variant="body2">
                  <b>Optional:</b>{' '}
                  {`${Math.max(0, teachersAssigned - minTeacherEval)}/${maxTeacherEval - minTeacherEval}`}
                </CoreTypography>
              </StyledTeacherCountGroup>
            </StyledTooltipBox>
          </StyledJustifiedBox>
          {teachersAssigned === 0 && (minTeacherEval > 0 || maxTeacherEval - minTeacherEval > 0) ? (
            <CoreTypography variant="body2">
              Please select and assign the Teacher(s) below whose recommendation will be submitted to this college on
              your behalf.
            </CoreTypography>
          ) : null}
          {member?.recommenders
            .filter((recommender) => recommender.personaType === 'Teacher')
            ?.map((teacher, index) =>
              applicant.teachers ? (
                <TeacherCard
                  key={`teacherCard${teacher.recommenderId}`}
                  index={index}
                  teacher={teacher}
                  memberId={member.memberId}
                  applicantTeachers={applicant.teachers}
                  handleRemove={onHandleRemoveTeacher}
                  handleEdit={onHandleEditTeacher}
                  isDisabled={showAddTeacher || !!showEditTeacher}
                />
              ) : null,
            )}
          {!showAddTeacher && !showEditTeacher && teachersAssigned < maxTeacherEval ? (
            <StyledTeacherButtonGroup alignSelf="flex-end">
              <ActionButton onClick={() => setShowAddTeacher(true)} startIcon={<AddIcon />}>
                Add Teacher
              </ActionButton>
            </StyledTeacherButtonGroup>
          ) : null}
          {showAddTeacher ? (
            <AddTeacher
              teacherRoles={teacherRolesNotAssigned}
              recommenders={recommendersNotAssigned}
              onAssignTeacher={handleAssignTeacher}
              onAddAndAssignTeacher={handleAddAndAssignTeacher}
              onCancel={() => setShowAddTeacher(false)}
            />
          ) : null}

          {showEditTeacher ? (
            <UpdateTeacher
              teacherRoles={teacherRolesNotAssigned}
              recommenders={recommendersNotAssigned}
              onCancel={() => setShowEditTeacher(null)}
              editTeacher={showEditTeacher}
              onEditTeacher={onHandleSaveTeacher}
            />
          ) : null}
        </>
      ) : null}
    </StyledRecommenders>
  ) : null;
};
